import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Button, Card, Input } from '@/components';
import { track } from '@/lib/analytics';
import { apiJson, ApiError } from '@/lib/api';
import type {
  AdminAppVersionDistributionResponse,
  AdminAppVersionWindow,
} from '@/lib/types';

type AppVersionConfigItem = {
  platform: string;
  latest_version: string | null;
  soft_wall_version: string | null;
  hard_wall_version: string | null;
  updated_at: string;
};

type PlatformFormState = {
  latest_version: string;
  soft_wall_version: string;
  hard_wall_version: string;
};

const PLATFORM_LABELS: Record<string, string> = {
  ios: 'iOS',
  android: 'Android',
};

const IOS_COLOR = '#018A9D';
const ANDROID_COLOR = '#6366f1';

function emptyForm(): PlatformFormState {
  return {
    latest_version: '',
    soft_wall_version: '',
    hard_wall_version: '',
  };
}

function formFromItem(item: AppVersionConfigItem | undefined): PlatformFormState {
  if (!item) return emptyForm();
  return {
    latest_version: item.latest_version ?? '',
    soft_wall_version: item.soft_wall_version ?? '',
    hard_wall_version: item.hard_wall_version ?? '',
  };
}

function parseDetail(err: unknown): string {
  if (err instanceof ApiError && err.bodyText) {
    try {
      const j = JSON.parse(err.bodyText) as { detail?: string | string[] };
      if (typeof j.detail === 'string') return j.detail;
      if (Array.isArray(j.detail)) return j.detail.map(String).join('; ');
    } catch {
      /* ignore */
    }
    return err.bodyText;
  }
  return err instanceof Error ? err.message : 'Request failed';
}

function nullableField(value: string): string | null {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function formatAdminDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function formatCount(value: number): string {
  return value.toLocaleString();
}

function formatPercent(value: number): string {
  return `${value.toLocaleString(undefined, {
    minimumFractionDigits: value % 1 === 0 ? 0 : 1,
    maximumFractionDigits: 1,
  })}%`;
}

function formatUtcDate(isoDate: string): string {
  const [year, month, day] = isoDate.split('-').map(Number);
  if (!year || !month || !day) return isoDate;
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

function formatWindowRange(window: AdminAppVersionWindow): string {
  if (window.start === window.end) {
    return `${formatUtcDate(window.start)} UTC`;
  }
  return `${formatUtcDate(window.start)} – ${formatUtcDate(window.end)} UTC`;
}

export const Route = createFileRoute('/admin/app-version')({
  component: AppVersionPage,
});

function AppVersionPage() {
  const { getToken } = useAuth();
  const qc = useQueryClient();

  useEffect(() => {
    track('Admin App Version Screen');
  }, []);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin', 'app-version'],
    queryFn: () =>
      apiJson<AppVersionConfigItem[]>(getToken, '/v1/admin/app-version'),
  });

  const distributionQuery = useQuery({
    queryKey: ['admin', 'app-version', 'distribution'],
    queryFn: () =>
      apiJson<AdminAppVersionDistributionResponse>(
        getToken,
        '/v1/admin/app-version/distribution',
      ),
  });

  const refreshAll = () => {
    void refetch();
    void qc.fetchQuery({
      queryKey: ['admin', 'app-version', 'distribution'],
      queryFn: () =>
        apiJson<AdminAppVersionDistributionResponse>(
          getToken,
          '/v1/admin/app-version/distribution?refresh=true',
        ),
    });
  };

  if (isLoading) {
    return <div className="py-12 text-center text-sm text-nova-muted-foreground">Loading…</div>;
  }

  if (error) {
    return (
      <p className="text-sm text-red-600">
        {parseDetail(error)}
        <button type="button" className="ml-2 underline" onClick={() => void refetch()}>
          Retry
        </button>
      </p>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-nova-foreground">App version</h1>
        <p className="mt-1 text-sm text-nova-muted-foreground">
          Per-platform thresholds returned by{' '}
          <code className="text-xs">GET /v1/app-version</code>. Leave a field blank to
          disable that tier.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {(['ios', 'android'] as const).map((platform) => (
          <PlatformConfigCard
            key={platform}
            platform={platform}
            item={data?.find((row) => row.platform === platform)}
            onSaved={() => {
              void qc.invalidateQueries({ queryKey: ['admin', 'app-version'] });
            }}
          />
        ))}
      </div>

      <div>
        <h2 className="text-lg font-medium text-nova-foreground">Active version mix</h2>
        <p className="mt-1 text-sm text-nova-muted-foreground">
          iOS and Android users who were active in Amplitude. We do not persist app
          version on API requests; the mobile SDK attaches it to analytics events.
          Someone who upgraded mid-window can appear on more than one version.
        </p>
      </div>

      {distributionQuery.isLoading ? (
        <div className="py-8 text-center text-sm text-nova-muted-foreground">Loading distribution…</div>
      ) : distributionQuery.isError || !distributionQuery.data ? (
        <p className="text-sm text-red-600">
          {distributionQuery.error
            ? parseDetail(distributionQuery.error)
            : 'Could not load version distribution.'}
          <button
            type="button"
            className="ml-2 underline"
            onClick={() => void distributionQuery.refetch()}
          >
            Retry
          </button>
        </p>
      ) : (
        <>
          {distributionQuery.data.error ? (
            <p className="text-sm text-amber-700">
              Showing empty totals: {distributionQuery.data.error}
            </p>
          ) : null}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <DistributionCard window={distributionQuery.data.last_24h} />
            <DistributionCard window={distributionQuery.data.last_7d} />
          </div>
        </>
      )}

      <Button variant="secondary" onClick={refreshAll}>
        Refresh
      </Button>
    </div>
  );
}

function DistributionCard({ window }: { window: AdminAppVersionWindow }) {
  return (
    <Card className="p-4">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-medium text-nova-foreground">{window.label}</h3>
          <p className="mt-0.5 text-xs text-nova-muted-foreground">{formatWindowRange(window)}</p>
        </div>
        <p className="text-right">
          <span className="block text-2xl font-semibold tabular-nums text-nova-foreground">
            {formatCount(window.unique_users)}
          </span>
          <span className="text-xs text-nova-muted-foreground">users</span>
        </p>
      </div>

      {window.versions.length ? (
        <>
          <VersionMixBar versions={window.versions} />
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-nova-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: IOS_COLOR }}
                aria-hidden
              />
              iOS
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: ANDROID_COLOR }}
                aria-hidden
              />
              Android
            </span>
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-nova-border text-left text-xs font-semibold uppercase tracking-wide text-nova-muted-foreground">
                  <th className="pb-2 pr-4 font-semibold">Version</th>
                  <th className="pb-2 pr-4 text-right font-semibold">iOS</th>
                  <th className="pb-2 pr-4 text-right font-semibold">Android</th>
                  <th className="pb-2 pr-4 text-right font-semibold">Total</th>
                  <th className="pb-2 text-right font-semibold">Share</th>
                </tr>
              </thead>
              <tbody>
                {window.versions.map((row) => (
                  <tr
                    key={row.version}
                    className="border-b border-nova-border last:border-0"
                  >
                    <td className="py-2 pr-4 font-mono text-xs text-nova-foreground">
                      {row.version}
                    </td>
                    <td className="py-2 pr-4 text-right tabular-nums">
                      {formatCount(row.ios)}
                    </td>
                    <td className="py-2 pr-4 text-right tabular-nums">
                      {formatCount(row.android)}
                    </td>
                    <td className="py-2 pr-4 text-right tabular-nums font-medium">
                      {formatCount(row.total)}
                    </td>
                    <td className="py-2 text-right tabular-nums text-nova-muted-foreground">
                      {formatPercent(row.percent)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <p className="text-sm text-nova-muted-foreground">No mobile users in this window.</p>
      )}
    </Card>
  );
}

function VersionMixBar({
  versions,
}: {
  versions: AdminAppVersionWindow['versions'];
}) {
  if (!versions.length) {
    return <div className="h-4 rounded-full bg-nova-muted" aria-hidden />;
  }

  return (
    <div
      className="flex h-4 overflow-hidden rounded-full bg-nova-muted"
      role="img"
      aria-label={versions
        .map((row) => `${row.version} ${formatPercent(row.percent)}`)
        .join(', ')}
    >
      {versions.map((row) => (
        <div
          key={row.version}
          className="flex h-full min-w-[2px]"
          style={{ width: `${Math.max(row.percent, 0)}%` }}
          title={`${row.version}: ${formatCount(row.total)} (${formatPercent(row.percent)})`}
        >
          {row.ios > 0 ? (
            <div
              className="h-full"
              style={{
                width: `${(row.ios / row.total) * 100}%`,
                backgroundColor: IOS_COLOR,
              }}
            />
          ) : null}
          {row.android > 0 ? (
            <div
              className="h-full"
              style={{
                width: `${(row.android / row.total) * 100}%`,
                backgroundColor: ANDROID_COLOR,
              }}
            />
          ) : null}
        </div>
      ))}
    </div>
  );
}

function PlatformConfigCard({
  platform,
  item,
  onSaved,
}: {
  platform: 'ios' | 'android';
  item: AppVersionConfigItem | undefined;
  onSaved: () => void;
}) {
  const { getToken } = useAuth();
  const [form, setForm] = useState<PlatformFormState>(() => formFromItem(item));
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    setForm(formFromItem(item));
  }, [item]);

  const saveMutation = useMutation({
    mutationFn: () =>
      apiJson<AppVersionConfigItem>(getToken, `/v1/admin/app-version/${platform}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          latest_version: nullableField(form.latest_version),
          soft_wall_version: nullableField(form.soft_wall_version),
          hard_wall_version: nullableField(form.hard_wall_version),
        }),
      }),
    onSuccess: () => {
      setSaveError(null);
      onSaved();
    },
    onError: (err) => {
      setSaveError(parseDetail(err));
    },
  });

  return (
    <Card className="h-full p-4">
      <h2 className="mb-1 text-lg font-medium text-nova-foreground">
        {PLATFORM_LABELS[platform]}
      </h2>
      <p className="mb-4 text-sm text-nova-muted-foreground">
        Hard wall blocks the app, soft wall nudges an update, latest marks the current store
        version.
      </p>

      <div className="space-y-4">
        <Input
          label="Latest version"
          placeholder="e.g. 4.2.0"
          value={form.latest_version}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, latest_version: e.target.value }))
          }
        />
        <Input
          label="Soft wall version"
          placeholder="e.g. 4.0.0"
          value={form.soft_wall_version}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, soft_wall_version: e.target.value }))
          }
        />
        <Input
          label="Hard wall version"
          placeholder="e.g. 3.5.0"
          value={form.hard_wall_version}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, hard_wall_version: e.target.value }))
          }
        />
      </div>

      {saveError ? <p className="mt-3 text-sm text-red-600">{saveError}</p> : null}

      <div className="mt-4 flex items-center gap-3">
        <Button
          onClick={() => void saveMutation.mutate()}
          disabled={saveMutation.isPending}
        >
          {saveMutation.isPending ? 'Saving…' : 'Save'}
        </Button>
        {item?.updated_at ? (
          <p className="text-xs text-nova-muted-foreground">
            Last updated {formatAdminDateTime(item.updated_at)}
          </p>
        ) : (
          <p className="text-xs text-nova-muted-foreground">Not saved yet</p>
        )}
      </div>
    </Card>
  );
}
